import type { CmmdForgeAutomationRunDetail as RunDetail } from "@cmmd-forge/contracts";
import { ExternalLinkIcon } from "lucide-react";

import { formatRelativeTimeLabel } from "~/timestampFormat";

export function humanizeAutomationValue(value: string): string {
  return value.replaceAll("_", " ");
}

function formatLatency(latencyMs: number | null): string {
  if (latencyMs === null) return "—";
  if (latencyMs < 1_000) return `${latencyMs}ms`;
  return `${(latencyMs / 1_000).toFixed(1)}s`;
}

function formatCost(costUsdMicros: number | null): string {
  if (costUsdMicros === null) return "—";
  return `$${(costUsdMicros / 1_000_000).toFixed(3)}`;
}

export function RunFailureNotice({ run }: { readonly run: RunDetail["run"] }) {
  if (!run.failureMessage) return null;
  return (
    <section className="mt-6 rounded-md border border-destructive-border bg-destructive-surface p-4">
      <h2 className="text-sm font-semibold text-destructive">Run stopped</h2>
      <p className="mt-1 text-sm text-destructive/90">{run.failureMessage}</p>
      {run.failureCode ? (
        <p className="mt-2 font-mono text-xs text-muted-foreground">{run.failureCode}</p>
      ) : null}
    </section>
  );
}

export function RunAttempts({ attempts }: { readonly attempts: RunDetail["attempts"] }) {
  return (
    <section>
      <h2 className="text-sm font-semibold">Stage attempts</h2>
      {attempts.length === 0 ? (
        <p className="mt-3 border-y border-border/60 py-4 text-sm text-muted-foreground">
          No stage attempts recorded.
        </p>
      ) : (
        <div className="mt-3 divide-y divide-border/60 border-y border-border/60">
          {attempts.map((attempt) => (
            <div key={attempt.id} className="grid gap-2 py-4 sm:grid-cols-[minmax(0,1fr)_auto]">
              <div className="min-w-0">
                <p className="text-sm font-medium capitalize">
                  {humanizeAutomationValue(attempt.stage)} · attempt {attempt.attempt}
                </p>
                <p className="mt-1 truncate text-xs text-muted-foreground">
                  {attempt.provider && attempt.model
                    ? `${attempt.provider} / ${attempt.model}`
                    : "Model not recorded"}
                </p>
                {attempt.outcome ? (
                  <p className="mt-2 text-sm text-muted-foreground">{attempt.outcome}</p>
                ) : null}
              </div>
              <div className="flex items-start gap-3 text-xs text-muted-foreground">
                <span className="capitalize">{attempt.status}</span>
                <span>{formatLatency(attempt.latencyMs)}</span>
                <span>{formatCost(attempt.costUsdMicros)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function RunArtifacts({ artifacts }: { readonly artifacts: RunDetail["artifacts"] }) {
  return (
    <section>
      <h2 className="text-sm font-semibold">Evidence</h2>
      {artifacts.length === 0 ? (
        <p className="mt-3 border-y border-border/60 py-4 text-sm text-muted-foreground">
          No evidence artifacts recorded.
        </p>
      ) : (
        <div className="mt-3 divide-y divide-border/60 border-y border-border/60">
          {artifacts.map((artifact) => (
            <div key={artifact.artifactId} className="flex items-center justify-between gap-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{artifact.name}</p>
                <p className="mt-1 text-xs text-muted-foreground capitalize">
                  {humanizeAutomationValue(artifact.artifactType)} ·{" "}
                  {humanizeAutomationValue(artifact.stage)}
                </p>
              </div>
              {artifact.uri ? (
                <a
                  href={artifact.uri}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex shrink-0 items-center gap-1 text-xs text-foreground underline-offset-4 hover:underline"
                >
                  Open <ExternalLinkIcon className="size-3.5" />
                </a>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function RunTimeline({ events }: { readonly events: RunDetail["events"] }) {
  return (
    <section>
      <h2 className="text-sm font-semibold">Timeline</h2>
      {events.length === 0 ? (
        <p className="mt-3 border-y border-border/60 py-4 text-sm text-muted-foreground">
          No timeline events recorded.
        </p>
      ) : (
        <ol className="mt-3 space-y-4 border-l border-border/70 pl-5">
          {events.map((event) => (
            <li key={event.id} className="relative">
              <span className="absolute top-1.5 -left-[23px] size-1.5 rounded-full bg-muted-foreground" />
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground capitalize">
                  {humanizeAutomationValue(event.stage)}
                </span>
                <span>attempt {event.attempt}</span>
                <span>{formatRelativeTimeLabel(event.createdAt)}</span>
              </div>
              <p className="mt-1 text-sm text-foreground/85">{event.message}</p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export function RunMetadata({ detail }: { readonly detail: RunDetail }) {
  const { run, rule, claim } = detail;
  const claimState = claim?.revokedAt
    ? "Revoked"
    : claim?.releasedAt
      ? "Released"
      : claim
        ? "Claimed"
        : "Unclaimed";
  return (
    <aside className="space-y-7 border-t border-border/60 pt-6 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-7">
      <section>
        <h2 className="text-xs font-medium text-muted-foreground">Execution</h2>
        <dl className="mt-3 space-y-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Worker</dt>
            <dd className="truncate text-right">{claim?.workerId ?? "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Runtime</dt>
            <dd className="truncate text-right">{claim?.runtimeId ?? "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Claim</dt>
            <dd>{claimState}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Heartbeat</dt>
            <dd className="text-right">
              {run.lastHeartbeatAt ? formatRelativeTimeLabel(run.lastHeartbeatAt) : "—"}
            </dd>
          </div>
        </dl>
      </section>
      <section>
        <h2 className="text-xs font-medium text-muted-foreground">Run policy</h2>
        <dl className="mt-3 space-y-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Mode</dt>
            <dd className="capitalize">{run.autonomyMode}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Model policy</dt>
            <dd className="truncate text-right">{rule.modelPolicyId}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Lock version</dt>
            <dd>{run.lockVersion}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Started</dt>
            <dd className="text-right">
              {run.startedAt ? formatRelativeTimeLabel(run.startedAt) : "—"}
            </dd>
          </div>
        </dl>
      </section>
    </aside>
  );
}
